import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseCommentInput } from "@/components/common/BaseCommentInput";
import { ExpandableText } from "@/components/common/ExpandableText";
import { MediaPreview } from "@/components/common/MediaPreview";
import { SendingBadge } from "@/components/common/SendingBadge";
import { UserAvatarWithPopup } from "@/components/common/UserAvatarWithPopup";
import { Card, CardContent } from "@/components/ui/card";
import { CommentEditForm } from "@/features/study-group-stream/components/CommentEditForm";
import { CommentHeaderActions } from "@/features/study-group-stream/components/CommentHeaderActions";
import { InteractionFooter } from "@/features/study-group-stream/components/InteractionFooter";
import { ReplyCard } from "@/features/study-group-stream/components/ReplyCard";
import {
  getEffectiveIsSending,
  getIsAnyActionLoading,
} from "@/features/study-group-stream/utils/commentUiState";
import { COMMENT_REACTION_OBJECT_MODEL } from "@/hooks/useCommentReactions";
import { useMentionInputNotice } from "@/hooks/useMentionInputNotice";
import { useStudyGroupMentionAvailableUsers } from "@/hooks/useStudyGroupMentionAvailableUsers";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import { convertTimezoneToLocal } from "@/lib/utils/dateUtils";

interface CommentCardProps {
  author?: string;
  content?: string;
  timestamp?: string;
  avatarUrl?: string | null;
  isInput?: boolean;
  postId?: number;
  commentId?: number;
  currentReaction?: ReactionType | null;
  onReaction?: (
    commentId: number,
    reaction: ReactionType | null,
  ) => Promise<void>;
  onAddComment?: (
    content: string,
    objectId: number | string,
    objectModel?: "post" | "comment",
    files?: File[],
  ) => Promise<unknown>;
  comment?: Comment;
  isPending?: boolean;
  isReply?: boolean;
  isReplyInput?: boolean;
  objectModel?: "post" | "comment";
  replies?: Comment[];
  onReplyClick?: (commentId: number) => void;
  reactingComments?: Set<number>;
  isSending?: boolean;
  onCommentSent?: () => void;
  isSubmitting?: boolean;
  autoFocus?: boolean;
  studyGroupId?: string | number | null;
  onDeleteComment?: (commentId: number) => Promise<void>;
  onEditComment?: (commentId: number, content: string) => Promise<void>;
  isCurrentUserAuthor?: boolean;
  currentUserId?: number | string;
  deletingComments?: Set<number>;
  editingComments?: Set<number>;
  onSendMessage?: (userId: number) => void;
  isCommentActive?: boolean;
  isHighlighted?: boolean;
  disabled?: boolean;
}

export const CommentCard: React.FC<CommentCardProps> = ({
  author,
  content,
  timestamp,
  avatarUrl,
  isInput = false,
  postId,
  commentId,
  currentReaction = null,
  onReaction,
  onAddComment,
  comment,
  isPending = false,
  isReply = false,
  isReplyInput = false,
  objectModel = "post",
  onReplyClick,
  replies,
  reactingComments = new Set<number>(),
  isSending = false,
  onCommentSent,
  isSubmitting = false,
  autoFocus = true,
  studyGroupId,
  onDeleteComment,
  onEditComment,
  isCurrentUserAuthor = false,
  currentUserId,
  deletingComments = new Set<number>(),
  editingComments = new Set<number>(),
  onSendMessage,
  isCommentActive = false,
  isHighlighted = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  // Extract properties from comment object if provided
  const displayAuthor =
    author ||
    comment?.user?.display_name ||
    comment?.createdBy?.display_name ||
    "";
  const displayContent = content || comment?.message || "";
  const displayTimestamp =
    timestamp || comment?.created_at || comment?.createdAt || "";
  const formattedTimestamp = (() => {
    if (!displayTimestamp) return "";
    const { date, time } = convertTimezoneToLocal(displayTimestamp, "UTC");
    if (!date || !time) return displayTimestamp;
    return `${date} ${time}`;
  })();
  const displayAvatarUrl =
    avatarUrl ||
    comment?.user?.image_url ||
    comment?.createdBy?.image_url ||
    "";
  const displayUserId = comment?.user?.id || comment?.createdBy?.id || 0;
  const displayCommentId = commentId || comment?.id || 0;
  const displayReaction =
    currentReaction || comment?.reactions?.current_user_emotion || null;
  const commentReplies = replies ?? comment?.comments ?? [];
  const hasReplies = commentReplies.length > 0;
  const effectiveIsSending = getEffectiveIsSending(comment, isSending);

  const isDeleting = deletingComments?.has(displayCommentId) || false;
  const isEditing = editingComments?.has(displayCommentId) || false;
  const isReacting = reactingComments?.has(displayCommentId) || false;

  const [_showReactions, setShowReactions] = useState(false);
  const [localReaction, setLocalReaction] = useState<ReactionType | null>(
    displayReaction,
  );
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedMessage, setEditedMessage] = useState(displayContent);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const mentionNoticeResetKey = isEditMode
    ? `${displayCommentId}:${displayContent}`
    : null;
  const hasTypedMentionInEdit = useMentionInputNotice(
    editedMessage,
    displayContent,
    mentionNoticeResetKey,
  );

  // メンション機能用フック
  const {
    searchMentionAvailableUsers,
    data: mentionUsers,
    isLoading: isLoadingMentionUsers,
  } = useStudyGroupMentionAvailableUsers(studyGroupId || null);

  // Update localReaction when displayReaction changes from parent
  useEffect(() => {
    setLocalReaction(displayReaction);
  }, [displayReaction]);

  useEffect(() => {
    setEditedMessage(displayContent);
  }, [displayContent]);

  useEffect(() => {
    if (!isEditing) {
      setIsEditMode(false);
    }
  }, [isEditing]);

  const handleReactionSelect = async (reaction: ReactionType) => {
    if (!displayCommentId || !onReaction || disabled) {
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowReactions(false);

    // Determine the new reaction state
    let newReactionState: ReactionType | null;

    if (localReaction === reaction) {
      // If clicking the same reaction, remove it
      newReactionState = null;
    } else {
      // Otherwise set the new reaction
      newReactionState = reaction;
    }

    // Update local state immediately for responsive UI
    setLocalReaction(newReactionState);

    // Call the parent handler with the new reaction state
    try {
      await onReaction(displayCommentId, newReactionState);
    } catch (error) {
      // Revert on error
      console.error("Reaction failed:", error);
      setLocalReaction(displayReaction);
    }
  };

  // BaseCommentInput用の送信ハンドラー
  const handleCommentSubmit = async (content: string, files?: File[]) => {
    if (!onAddComment) {
      console.error("Missing onAddComment handler");
      return;
    }

    if (!postId && !displayCommentId) {
      console.error("Missing postId or commentId for comment submission");
      return;
    }

    try {
      const targetId = postId || displayCommentId;
      if (!targetId) {
        console.error("No target ID available for comment submission");
        return;
      }

      const targetObjectModel = isReplyInput ? "comment" : objectModel;

      await onAddComment(content, targetId, targetObjectModel, files);

      if (onCommentSent) {
        onCommentSent();
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
      throw error;
    }
  };

  // Modified handleReplyButtonClick to only handle reply creation, not toggling visibility
  const handleReplyButtonClick = () => {
    if (onReplyClick && displayCommentId && !disabled) {
      onReplyClick(displayCommentId);
    }
  };

  const handleEditClick = (_commentId: number) => {
    setIsEditMode(true);
    setEditedMessage(displayContent);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedMessage(displayContent);
  };

  const handleSubmitEdit = async () => {
    const trimmedMessage = editedMessage.trim();

    if (!onEditComment || trimmedMessage === displayContent.trim()) {
      setIsEditMode(false);
      return;
    }

    if (trimmedMessage === "") {
      alert(t("common.messageCannotBeEmpty"));
      return;
    }

    setIsEditMode(false);
    setIsSubmittingEdit(true);

    try {
      await onEditComment(displayCommentId, trimmedMessage);
    } catch (error) {
      console.error("Failed to edit comment:", error);
      alert(t("common.editError"));
      setEditedMessage(displayContent);
      setIsEditMode(true);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleDeleteClick = async (commentId: number) => {
    if (!onDeleteComment) {
      console.error(" CommentCard: onDeleteComment handler is missing!");
      return;
    }

    try {
      await onDeleteComment(commentId);
    } catch (error) {
      console.error(" CommentCard: Failed to delete comment:", error);
    }
  };

  const renderInputCard = () => (
    <Card
      className={cn(
        "flex w-full flex-col border-none shadow-none",
        isReplyInput ? "stream-bg-comment rounded-tr-xl" : "bg-transparent",
      )}
    >
      <CardContent className="p-3">
        <BaseCommentInput
          onSubmit={handleCommentSubmit}
          onMentionTrigger={searchMentionAvailableUsers}
          mentionUsers={mentionUsers}
          isLoadingMentionUsers={isLoadingMentionUsers}
          autoFocus={autoFocus}
          disabled={isSubmitting}
        />
      </CardContent>
    </Card>
  );

  if (isInput) {
    return renderInputCard();
  }

  // Apply different styles for replies
  const replyMarginClass = isReply ? "ml-8" : "";

  const isAnyActionLoading = getIsAnyActionLoading({
    isDeleting,
    isSubmittingEdit,
    isSending: effectiveIsSending,
  });

  return (
    <Card
      data-comment-id={displayCommentId}
      className={cn(
        "mt-1 flex w-full min-w-0 flex-col overflow-visible rounded-tr-xl border-none shadow-none",
        isHighlighted ? "bg-amber-50" : "stream-bg-comment",
        effectiveIsSending && "border-l-4 border-blue-500 bg-blue-50",
        isAnyActionLoading && "opacity-70",
        replyMarginClass,
      )}
    >
      <CardContent className="flex w-full min-w-0 flex-col gap-2 overflow-visible py-3 pl-3">
        <div className="flex w-full min-w-0 items-start gap-2">
          <UserAvatarWithPopup
            userId={displayUserId}
            userName={displayAuthor}
            avatarUrl={displayAvatarUrl}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            wrapperClassName="h-8 w-8 flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80"
            avatarClassName="h-8 w-8 flex-shrink-0"
            avatarFallbackClassName="text-sm"
          />
          <div className="flex w-full min-w-0 flex-1 flex-col overflow-visible">
            <div className="comment-header-offset flex min-w-0 items-center justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold text-variable-collection-black-gray">
                  {displayAuthor}
                </span>
                <span className="shrink-0 text-xs font-medium text-rw-dark-gray">
                  {formattedTimestamp}
                </span>
                {effectiveIsSending && <SendingBadge />}
              </div>
              {!disabled && (
                <CommentHeaderActions
                  commentId={displayCommentId}
                  isCurrentUserAuthor={isCurrentUserAuthor}
                  isAnyActionLoading={isAnyActionLoading}
                  isDeleting={isDeleting}
                  onEditClick={handleEditClick}
                  onDeleteClick={handleDeleteClick}
                />
              )}
            </div>
            {(() => {
              if (isEditMode) {
                return (
                  <div className="comment-body-offset">
                    <CommentEditForm
                      value={editedMessage}
                      isSubmitting={isSubmittingEdit}
                      onChange={setEditedMessage}
                      onSave={handleSubmitEdit}
                      onCancel={handleCancelEdit}
                      showMentionWarning={hasTypedMentionInEdit}
                      cancelButtonVariant="outline"
                    />
                  </div>
                );
              }
              return (
                <div className="comment-body-offset">
                  <ExpandableText content={displayContent} />

                  {/* Display file attachments if present */}
                  {comment?.files && comment.files.length > 0 && (
                    <div className="mt-2">
                      <MediaPreview files={comment.files} />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <InteractionFooter
          localReaction={localReaction}
          isReacting={isReacting}
          isPending={isPending}
          commentId={displayCommentId}
          onReactionSelect={handleReactionSelect}
          onReplyClick={handleReplyButtonClick}
          showReplyButton={true}
          isAnyActionLoading={isAnyActionLoading}
          reactionTotal={comment?.reactions?.total ?? 0}
          lastReactions={comment?.reactions?.last_reactions ?? []}
          reactionBreakdown={comment?.reactions?.by_emotion}
          reactionObjectModel={COMMENT_REACTION_OBJECT_MODEL}
          reactionObjectId={displayCommentId ?? undefined}
          isCommentActive={isCommentActive}
          disabled={disabled}
        />

        {/* Show replies if available */}
        {hasReplies && (
          <div className="mt-2 w-full max-w-full overflow-hidden rounded bg-transparent">
            {commentReplies.map((reply) => {
              const isReplyAuthor = (() => {
                if (!currentUserId) return false;
                const replyUserId = reply.user?.id || reply.createdBy?.id;
                const isAuthor = String(replyUserId) === String(currentUserId);
                return isAuthor;
              })();

              return (
                <ReplyCard
                  key={reply.id}
                  comment={reply}
                  onReaction={onReaction}
                  isReacting={reactingComments.has(reply.id)}
                  onReplyClick={onReplyClick}
                  isPending={reply.isPending}
                  isSending={reply.isSending}
                  onDeleteComment={onDeleteComment}
                  onEditComment={onEditComment}
                  isDeleting={deletingComments?.has(reply.id) || false}
                  isCurrentUserAuthor={isReplyAuthor}
                  currentUserId={currentUserId}
                  onSendMessage={onSendMessage}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
